// Single source of all ET formatting on the frontend.
// Mirrors src/wxrelay/time.py — UI never calls toLocaleString ad-hoc.
//
// Backend already sends ET-formatted ISO strings in the `iso` field of
// every TimePair. We pass them through as-is for display, and use the
// `utc` sibling for computing ages / sorting.

const ET_TZ = "America/New_York";

const fmtFull = new Intl.DateTimeFormat("en-US", {
  timeZone: ET_TZ,
  year: "numeric", month: "2-digit", day: "2-digit",
  hour: "2-digit", minute: "2-digit", second: "2-digit",
  hour12: false,
});

const fmtTime = new Intl.DateTimeFormat("en-US", {
  timeZone: ET_TZ,
  hour: "2-digit", minute: "2-digit", second: "2-digit",
  hour12: false,
});

const fmtDate = new Intl.DateTimeFormat("en-US", {
  timeZone: ET_TZ,
  year: "numeric", month: "2-digit", day: "2-digit",
});

function partsOf(d: Date, fmt: Intl.DateTimeFormat): Record<string, string> {
  return Object.fromEntries(fmt.formatToParts(d).map((p) => [p.type, p.value]));
}

export function formatET(input: string | Date): string {
  const d = typeof input === "string" ? new Date(input) : input;
  if (isNaN(d.getTime())) return "—";
  const p = partsOf(d, fmtFull);
  return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}:${p.second} ET`;
}

export function formatETTime(input: string | Date): string {
  const d = typeof input === "string" ? new Date(input) : input;
  if (isNaN(d.getTime())) return "—";
  const p = partsOf(d, fmtTime);
  return `${p.hour}:${p.minute}:${p.second}`;
}

export function formatETDate(input: string | Date): string {
  const d = typeof input === "string" ? new Date(input) : input;
  if (isNaN(d.getTime())) return "—";
  const p = partsOf(d, fmtDate);
  return `${p.year}-${p.month}-${p.day}`;
}

export function ageSeconds(input: string | Date): number {
  const d = typeof input === "string" ? new Date(input) : input;
  return (Date.now() - d.getTime()) / 1000;
}

/** Format a UTC ISO string as HH:MM in the given IANA timezone, e.g.
 *  "07:53". Returns "—" for unparseable input. */
export function formatTimeInTz(input: string | Date, tz: string): string {
  const d = typeof input === "string" ? new Date(input) : input;
  if (isNaN(d.getTime())) return "—";
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: tz, hour: "2-digit", minute: "2-digit", hour12: false,
  });
  const p = Object.fromEntries(fmt.formatToParts(d).map((x) => [x.type, x.value]));
  return `${p.hour}:${p.minute}`;
}

/** Short tz abbreviation derived from IANA name. "America/Los_Angeles"
 *  -> "PT"; falls back to last segment if unknown. */
export function shortTz(tz: string): string {
  const map: Record<string, string> = {
    "America/Los_Angeles": "PT",
    "America/Denver": "MT",
    "America/Chicago": "CT",
    "America/New_York": "ET",
    "America/Phoenix": "MST",
    "America/Anchorage": "AKT",
    "Pacific/Honolulu": "HST",
  };
  return map[tz] ?? tz.split("/").pop() ?? tz;
}

export function formatAge(seconds: number): string {
  if (!isFinite(seconds)) return "—";
  if (seconds < 60) return `${seconds.toFixed(0)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.floor(seconds % 60)}s`;
  if (seconds < 86400) return `${(seconds / 3600).toFixed(1)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}
